import { Autocomplete, TextField, createFilterOptions } from '@mui/material';
import { useAtom } from 'jotai';
import { noop } from 'lodash';
import { useRef, useState } from 'react';
import { interviewAtom, setupDictionary } from '../../../stores/jotai/interviews/currentInterview';
import { updateTranscriptData } from '../../../util/transcriptionUpdateUtils';

interface IOption {
    id: string;
    label: string;
    inputValue?: string;
}

const filter = createFilterOptions<IOption>();

export default function MaterialSpeakerPopup(props: any) {
    const { slateData, onChange = noop } = props;
    const inputRef = useRef(null);
    const [searchValue, setSearchValue] = useState('');
    const [interviewState, setInterviewState] = useAtom(interviewAtom);
    const { speakerLibrary, speakerDictionary } = interviewState;

    const saveSpeaker = (newSpeakerDictionary, onSuccess = noop) => {
        updateTranscriptData({
            transcriptS3ObjectID: props?.transcriptDataURL,
            data: {
                type: "transcript",
                data: {
                    speakerDictionary: newSpeakerDictionary,
                    speakerLibrary: speakerLibrary,
                    slate: slateData,
                },
            },
            onSuccess: () => {
                setupDictionary(newSpeakerDictionary, setInterviewState);
                onSuccess();

            },
        });
    };

    const getSpeakerOptionsShow = () => {
        const options = props?.speakerDictionary?.map(
            (speaker) => ({
                id: speaker?.id,
                label: speaker?.name,
            })
        );

        return options;
    }

    const getSelectedValue = () => {
        if (!props.value) {
            return null;
        }

        const speaker = props?.speakerDictionary?.find(
            (speaker) => speaker?.id === props?.value
        );

        return {
            id: speaker?.id,
            label: speaker?.name,
        };
    }

    return (
        <Autocomplete
            autoFocus={true}
            onClose={(event: any, reason: any) => {
                if (props.onClose) {
                    props.onClose();
                }
            }}
            onOpen={() => {
                if (props.onOpen) {
                    props.onOpen();
                }
            }}
            onChange={(event: any, newValue: IOption | null) => {
                if (newValue?.inputValue) {
                    const valueToAdd = {
                        id: newValue?.inputValue,
                        name: newValue?.inputValue,
                    };
                    const newSpeakerDictionary = [
                        ...speakerDictionary,
                        valueToAdd,
                    ];

                    saveSpeaker(newSpeakerDictionary, () => {
                        onChange({
                            ...valueToAdd,
                            label: valueToAdd.name,
                        });
                    });
                } else {
                    onChange(newValue);
                }
            }}
            clearOnEscape={true}
            value={getSelectedValue()}
            isOptionEqualToValue={(option, value) => {
                return option?.id === value.id;
            }}
            sx={{
                width: "auto",
                ".MuiOutlinedInput-notchedOutline": {
                    border: "none",
                },
                ".MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
                    minWidth: "70px",
                },
            }}
            size="small"
            disableClearable={true}
            options={getSpeakerOptionsShow()}
            inputValue={searchValue}
            onFocus={() => {
                if (props.onFocus) {
                    props.onFocus();
                }
            }}
            onInputChange={(event, newInputValue) => {
                setSearchValue(newInputValue);
            }}
            renderInput={(params) => {
                return (
                    <TextField
                        inputRef={inputRef}
                        {...params}
                        placeholder={props.placeholder}
                    />
                );
            }}
            filterOptions={(options, params) => {
                const filtered = filter(options, params);
                const { inputValue } = params;
                const isExisting = options?.some(
                    (option) => inputValue === option.label
                );

                if (inputValue !== "" && !isExisting) {
                    filtered.push({
                        inputValue,
                        id: inputValue,
                        label: `Add "${inputValue}"`,
                    });
                }

                return filtered;
            }}
            getOptionLabel={(option: IOption) => {
                if (typeof option === "string") {
                    return option;
                }

                if (option.inputValue) {
                    return option.inputValue;
                }

                return option.label;
            }}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props;

                return (
                    <li
                        key={key}
                        {...optionProps}
                    >
                        {option?.label}
                    </li>
                );
            }}
            disabled={props.disabled}
        />
    );
}
